import { useEffect, useState } from 'react';
import * as Collapsible from '@radix-ui/react-collapsible';
import Icon from '@/components/ui/Icon';
import classnames from 'classnames';
import Button from '@/components/ui/Buttons/Button';
import TextInput from '../Form/TextInput';
import { Root } from '@radix-ui/react-form';
import { ICartViewModel } from '@/@types/client-api';
import { AnalyticsContext } from '@/analytics/constants';

interface PromoCodeInputProps {
  cart?: ICartViewModel;
  context?: AnalyticsContext;
  applyPromoCode?: (code: string, cart?: ICartViewModel, context?: AnalyticsContext) => void;
  isApplyingPromoCode?: boolean;
  inputErrorMessage?: string;
  className?: string;
}

export const PromoCodeInput: React.FC<PromoCodeInputProps> = ({
  cart,
  context,
  applyPromoCode,
  isApplyingPromoCode,
  inputErrorMessage,
  className
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [lastSubmittedCode, setLastSubmittedCode] = useState('');

  const handleApply = () => {
    if (promoCode && applyPromoCode && !isApplyingPromoCode) {
      setIsOpen(false);
      setLastSubmittedCode(promoCode);
      applyPromoCode(promoCode, cart, context);
      setPromoCode('');
    }
  };

  useEffect(() => {
    if (inputErrorMessage) {
      setIsOpen(true);
      setPromoCode(lastSubmittedCode);
    }
  }, [inputErrorMessage, lastSubmittedCode]);

  const triggerClasses = classnames('flex items-center hover:text-red focus:outline-none');

  return (
    <div className={className} data-component="PromoCodeInput">
      <Collapsible.Root open={isOpen} onOpenChange={setIsOpen}>
        <Collapsible.Trigger className={triggerClasses}>
          <span className="text-2-bold mr-2">Add a promo code</span>
          <Icon name={isOpen ? 'chevron-up' : 'chevron-down'} size="small" />
        </Collapsible.Trigger>

        <Collapsible.Content>
          <Root
            className="mb-5 mt-4 flex w-full flex-col items-stretch gap-6 sm:flex-row lg:gap-3"
            onSubmit={e => {
              e.preventDefault();
              handleApply();
            }}
          >
            <TextInput
              className="w-full"
              variant="container"
              label="Promo code"
              value={promoCode}
              errorValidationEnabled={true}
              forceInvalid={!!inputErrorMessage}
              errors={[{ message: inputErrorMessage, forceMatch: true }]}
              onChange={e => setPromoCode(e)}
            />
            <Button
              type="button"
              onClick={() => handleApply()}
              color="outline-black"
              label="Apply Code"
              size="large"
              className="h-[70px]"
            />
          </Root>
        </Collapsible.Content>
      </Collapsible.Root>
    </div>
  );
};
